// Recipe AI capture mode — paste a recipe (URL, ingredient list, or
// pasted block) + optional servings → AI parses → returns a per-serving
// estimate that the user can review, adjust portions, and log.
//
// Hands the result off via onSubmit({ estimate, name }) so Capture.tsx's
// existing state machine routes through MealReview as if it were any
// other AI estimate.

import { useState, type FormEvent } from "react";
import { ChefHat, Loader2 } from "lucide-react";
import { fetchRecipeEstimate } from "../lib/ai";
import { friendlyError } from "../lib/errors";
import type { MealEstimate } from "../lib/types";

interface Props {
  busy: boolean;
  onSubmit: (input: { estimate: MealEstimate; name: string }) => void;
}

export default function RecipeCapture({ busy, onSubmit }: Props) {
  const [text, setText] = useState("");
  const [servings, setServings] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [localBusy, setLocalBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setLocalBusy(true);
    setError(null);
    try {
      const parsedServings = servings.trim() ? parseInt(servings.trim(), 10) : undefined;
      const result = await fetchRecipeEstimate(trimmed, parsedServings);
      onSubmit({ estimate: result.per_serving, name: result.name });
      // Parent transitions to MealReview; we don't need to clear local state.
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLocalBusy(false);
    }
  }

  const disabled = busy || localBusy || !text.trim();

  return (
    <form onSubmit={submit} className="p-4 space-y-4">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-violet-950/30 border border-violet-900/60">
        <ChefHat className="w-5 h-5 text-violet-300 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-violet-100 leading-relaxed">
          Paste a recipe — ingredients list, instructions, or both — and AI returns <span className="font-semibold">per-serving</span> macros + nutrients. Works with copy-pasted recipes from any blog or site.
        </div>
      </div>

      <label className="block">
        <span className="text-sm text-slate-400">Recipe text</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Paste an ingredient list and any cooking notes, e.g.:\n\nServes 4\n• 1 lb spaghetti\n• 1 lb ground beef\n• 1 jar marinara sauce\n• 1/2 cup parmesan"}
          rows={8}
          maxLength={8000}
          required
          className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 text-sm resize-y"
        />
        <div className="flex justify-end text-[11px] text-slate-500 mt-1 tabular-nums">
          {text.length} / 8000
        </div>
      </label>

      <label className="block">
        <span className="text-sm text-slate-400">
          Servings <span className="text-slate-500">(optional — overrides AI detection)</span>
        </span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={100}
          value={servings}
          onChange={(e) => setServings(e.target.value)}
          placeholder="e.g. 4"
          className="mt-1 w-32 bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 text-center tabular-nums"
        />
      </label>

      {error && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={disabled}
        className="w-full py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {localBusy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Parsing recipe…
          </>
        ) : (
          "Parse recipe →"
        )}
      </button>

      <p className="text-[11px] text-slate-500 text-center">
        After parsing you can review + adjust the per-serving values before logging.
      </p>
    </form>
  );
}
